import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Trash2, X } from 'lucide-react';
import api from '../../lib/api';
import { messageHandler } from '../../services/message-handler.service';
import type { Contact } from '../../types/contact';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  FormInput,
  Modal,
} from '../ui';
import ContactForm from './ContactForm';
import LoadingSpinner from '../ui/LoadingSpinner';

interface ContactManagerProps {
  contacts: Contact[];
  isLoading: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSelectContact: (contact: Contact) => void;
  onClose: () => void;
  showCreateButton?: boolean;
}

function ContactManager({
  contacts,
  isLoading,
  searchTerm,
  onSearchChange,
  onSelectContact,
  onClose,
  showCreateButton = true,
}: ContactManagerProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const queryClient = useQueryClient();

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<Contact>) => api.post('/contacts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      messageHandler.success('Contato criado com sucesso!');
      setIsCreateModalOpen(false);
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Contact> }) =>
      api.put(`/contacts/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      messageHandler.success('Contato atualizado com sucesso!');
      setEditingContact(null);
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/contacts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      messageHandler.success('Contato excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      contact: formData.get('contact') as string,
      name: formData.get('name') as string,
      is_custom_name: formData.get('is_custom_name') === 'on',
      channel: formData.get('channel') as
        | 'whatsapp'
        | 'instagram'
        | 'telegram'
        | 'facebook'
        | 'email'
        | 'other',
    };

    if (editingContact) {
      updateMutation.mutate({ id: editingContact.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (contact: Contact) => {
    messageHandler.confirm('Excluir Contato', 'Tem certeza que deseja excluir este contato?', () =>
      deleteMutation.mutate(contact.id)
    );
  };

  return (
    <div>
      <div className="mb-4 flex gap-2">
        <FormInput
          placeholder="Buscar contato..."
          value={searchTerm}
          onChange={e => onSearchChange(e.target.value)}
          className="flex-1"
        />
        {showCreateButton && (
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Contato
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      ) : contacts && contacts.length > 0 ? (
        <div className="max-h-96 overflow-y-auto">
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Canal</TableHead>
                  <TableHead className="relative">
                    <span className="sr-only">Ações</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact: Contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>{contact.name || '-'}</TableCell>
                    <TableCell>{contact.contact}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{contact.channel}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="ghost"
                          onClick={() => onSelectContact(contact)}
                          title="Selecionar"
                        >
                          <Plus className="h-4 w-4 text-primary-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => setEditingContact(contact)}
                          title="Editar"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-pencil"
                          >
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                            <path d="m15 5 4 4" />
                          </svg>
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleDelete(contact)}
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : (
        <div className="text-center py-4 border border-dashed rounded-md">
          <p className="text-gray-500">Nenhum contato encontrado</p>
          {showCreateButton && (
            <Button variant="link" onClick={() => setIsCreateModalOpen(true)}>
              Criar novo contato
            </Button>
          )}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>

      {/* Create Contact Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Novo Contato"
      >
        <ContactForm
          contact={null}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending}
          onClose={() => setIsCreateModalOpen(false)}
        />
      </Modal>

      {/* Edit Contact Modal */}
      <Modal
        isOpen={!!editingContact}
        onClose={() => setEditingContact(null)}
        title="Editar Contato"
      >
        <ContactForm
          contact={editingContact}
          onSubmit={handleSubmit}
          isLoading={updateMutation.isPending}
          onClose={() => setEditingContact(null)}
        />
      </Modal>
    </div>
  );
}

export default ContactManager;
